import { useContext } from "react";
import { CellContext } from "@tanstack/react-table";
import { VendorContext } from "@client-shared/providers/vendor";
import { useProductsState } from "@client-shared/providers/products";
import { useConfigState } from "@client-shared/providers/config";
import { formatPrice, parsePrice } from "@bcc/lib/utils";
import { isPriceUpdateDisabledForRegion } from "@bcc/lib/products/price-update-region";
import { displayWholesaleForRegion, parseBagSizeToKg, resolveHomeRegionPricingContext } from "@bcc/lib/products/cross-border-tax";
import { resolveShipToPrice, resolveShipToWholesalePrice } from "@bcc/lib/products/ship-to-countries";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import type { RoasterApprovalRequestDraft } from "@shared/services/salesforce/types";
import { Pencil } from "lucide-react";
import { VariantWithProductData } from "../types";
import { setWholesalePriceCellTestId, setWholesalePriceEditButtonTestId, homeRegionForVendor } from "../products-table.utils";
import { PriceUpdatePopover } from "../../price-update-popover";
import type { PriceUpdateVariant } from "../../price-update-popover";

interface WholesalePriceCellProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WholesalePriceCellMarkup = (props: CellContext<VariantWithProductData, unknown> & WholesalePriceCellProps) => {
  const data = props.row.original;
  const [{ currencyObject, currentVendor }] = useContext(VendorContext);
  const [{ config }] = useConfigState();
  const [, , productsProviderMethods] = useProductsState();
  const ratesByRegion = useTaxRatesContext();

  // Cross-border wholesale is stored KS-excluded (netto + VAT); so is DE-domestic
  // wholesale. Reconstruct the coffee-tax-inclusive gross for display
  // using the roaster's countryCode; for non-cross-border, non-DE-domestic SKUs
  // and KS-free regions (e.g. NL) the value is unchanged.
  const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
  const rawShipTo = data.shipTo?.map((country) => country.countryCode) ?? [];
  const { shipTo: effectiveShipTo, showRegionPricingUi } = resolveHomeRegionPricingContext(homeRegion, rawShipTo);
  // The multi-channel PIM fetch carries per-country prices in `shipTo` only and
  // leaves the variant's top-level price / wholesalePrice empty. Fall back to the
  // home-region ship-to entry (then the first) so the scalar column and the
  // price-update popover show real values instead of 0.
  const homeShipTo = data.shipTo?.find((country) => country.countryCode === homeRegion) ?? data.shipTo?.[0];
  // The logged-in country's per-channel `shipToInfo` price wins: on PIM-first
  // rows it is the only value an approved Price Update reaches, while the flat
  // `data.wholesalePrice` comes from the Salesforce overlay and can be stale.
  const baseWholesale = resolveShipToWholesalePrice(
    data.shipToInfo,
    currentVendor?.countryCode,
    data.wholesalePrice ?? homeShipTo?.wholesalePrice
  );
  const basePrice = resolveShipToPrice(data.shipToInfo, currentVendor?.countryCode, data.price ?? homeShipTo?.retailPrice);
  const displayWholesale = showRegionPricingUi
    ? displayWholesaleForRegion(parsePrice(baseWholesale), homeRegion, ratesByRegion, parseBagSizeToKg(data.bagSize))
    : baseWholesale;
  const formatted = formatPrice(displayWholesale, currencyObject?.symbol, currencyObject?.iso);

  if (
    !data.skuCode ||
    !currentVendor?.vendorKey ||
    !productsProviderMethods ||
    data.hasPendingPriceApproval ||
    isPriceUpdateDisabledForRegion(currentVendor.region) ||
    config.actionsConfig?.PriceUpdateActionAllowed === false
  ) {
    return (
      <span className="text-sm" data-testid={setWholesalePriceCellTestId(props.row.index)}>
        {formatted}
      </span>
    );
  }

  const variant: PriceUpdateVariant = {
    id: data.id,
    size: data.bagSize ?? "",
    sku: data.skuCode,
    vendorKey: currentVendor.vendorKey,
    productName: data.productTitle ?? "",
    coffeeTerm: data.term === "Short" ? "Short" : "Long",
    msrp: parsePrice(basePrice),
    wholesalePrice: parsePrice(baseWholesale),
    // Roaster's own country drives roasterCountryCode and the single ship-to
    // entry for non-cross-border SKUs.
    roasterCountryCode: currentVendor.countryCode ?? homeRegion ?? "",
    // Existing weekly inventory the backend requires on each price-update entry.
    weeklyInventory: data.weeklyInventory ?? data.maxBagsShippedPerWeek,
    // Per-country current prices for cross-border SKUs (channel → country).
    shipToInfo: data.shipToInfo,
    // Cross-border: drives the home-region-editable / others-derived popover
    // layout. Empty until the product fetch populates per-country ship-to data,
    // except for DE-domestic, which is synthesized to [homeRegion].
    shipTo: effectiveShipTo,
    homeRegion,
  };

  const handleUpdatePrice = (request: RoasterApprovalRequestDraft) => productsProviderMethods.handleUpdateSkuStatus([request]);

  return (
    <div data-testid={setWholesalePriceCellTestId(props.row.index)}>
      <PriceUpdatePopover
        variant={variant}
        onUpdatePrice={handleUpdatePrice}
        isUpdating={productsProviderMethods.isUpdatingSkuStatus}
        ratesByRegion={ratesByRegion}
        marginFloorPercent={config.marginConfig?.marginPercentage}
        open={props.open}
        onOpenChange={props.onOpenChange}
      >
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer"
          data-testid={setWholesalePriceEditButtonTestId(props.row.index)}
        >
          {formatted}
          <Pencil className="h-3 w-3" />
        </button>
      </PriceUpdatePopover>
    </div>
  );
};
